// Per-item client-side validation. Mirror of backend
// backend/src/domains/listing/itemValidator.js. The backend is authoritative —
// this exists so the wizard can block "Continue" / "Publish" with a friendly
// error before the API call, and render per-field asterisks.

import { CATEGORY_METADATA, getCategoryMeta } from '../constants/categoryMetadata';

const NUMERIC_FIELDS = new Set([
  'weight',
  'quantity',
  'year',
  'lengthMeters',
  'totalVolumeCbm',
  'originFloor',
  'destFloor',
]);

const BOOLEAN_FIELDS = new Set(['hasElevatorOrigin', 'hasElevatorDest']);

const isValidString = (value) =>
  typeof value === 'string' && value.trim().length > 0;

const isValidDimensions = (value) => {
  if (!value || typeof value !== 'object') return false;
  const l = Number(value.l);
  const w = Number(value.w);
  const h = Number(value.h);
  return (
    Number.isFinite(l) && l > 0 &&
    Number.isFinite(w) && w > 0 &&
    Number.isFinite(h) && h > 0
  );
};

const hasValue = (item, field) => {
  const value = item?.[field];
  return value !== undefined && value !== null && value !== '';
};

// 'missing' | 'invalid' | 'ok'
function classifyField(item, field) {
  if (!hasValue(item, field)) return 'missing';

  if (field === 'dimensions') {
    return isValidDimensions(item.dimensions) ? 'ok' : 'invalid';
  }

  if (field === 'year') {
    const num = Number(item.year);
    return Number.isFinite(num) && num > 1900 ? 'ok' : 'invalid';
  }

  if (NUMERIC_FIELDS.has(field)) {
    const allowZero = field === 'originFloor' || field === 'destFloor';
    const num = Number(item[field]);
    if (!Number.isFinite(num)) return 'invalid';
    if (!allowZero && num <= 0) return 'invalid';
    if (allowZero && num < 0) return 'invalid';
    return 'ok';
  }

  if (BOOLEAN_FIELDS.has(field)) {
    return typeof item[field] === 'boolean' ? 'ok' : 'invalid';
  }

  return isValidString(item[field]) ? 'ok' : 'invalid';
}

/**
 * Validate a single item against the category schema.
 * Returns { valid, missing: [...], invalid: [...] }.
 */
export function validateItem(category, item) {
  const meta = getCategoryMeta(category);
  const missing = [];
  const invalid = [];

  meta.requiredItemFields.forEach((field) => {
    const result = classifyField(item, field);
    if (result === 'missing') missing.push(field);
    else if (result === 'invalid') invalid.push(field);
  });

  return { valid: missing.length === 0 && invalid.length === 0, missing, invalid };
}

/**
 * Validate an items[] array. Returns { valid, errors: [{ index, missing, invalid }] }.
 * Intended for wizard blocking before "Continue" / "Publish".
 */
export function validateItemsForCategory(category, items) {
  if (!Array.isArray(items) || items.length === 0) {
    return {
      valid: false,
      errors: [{ index: 0, missing: ['items'], invalid: [] }],
    };
  }

  const errors = [];
  items.forEach((item, index) => {
    const { valid, missing, invalid } = validateItem(category, item);
    if (!valid) errors.push({ index, missing, invalid });
  });

  return { valid: errors.length === 0, errors };
}

/**
 * Build a human-readable error message from the result of validateItemsForCategory.
 * Example: `Item 1 missing: vin, year. Item 2 invalid: weight.`
 */
export function formatValidationErrors(result) {
  if (!result || result.valid) return '';
  return result.errors
    .map(({ index, missing, invalid }) => {
      const parts = [];
      if (missing.length) parts.push(`missing: ${missing.join(', ')}`);
      if (invalid.length) parts.push(`invalid: ${invalid.join(', ')}`);
      return `Item ${index + 1} ${parts.join('; ')}.`;
    })
    .join(' ');
}

export { CATEGORY_METADATA };
